import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Upload, FileText, AlertTriangle, CheckCircle2, Import } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  extractHeader,
  parseFlightTables,
  validateRows,
  type ScheduleHeader,
  type ValidatedFlightPatternRow,
} from "@/utils/scheduleMarkdownParser";
import { importSchedule, type ImportProgress } from "@/services/scheduleImportService";
import { resolveQuestionMarkTimes } from "@/utils/scheduleTimeResolver";
import ScheduleLibrary from "@/components/schedule/ScheduleLibrary";
import CacheStatus from "@/components/schedule/CacheStatus";

async function generateFileHash(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

interface DuplicateInfo {
  schedule_version: string;
  airline_code: string;
  imported_at: string;
}

export default function ScheduleToolPage() {
  const [file, setFile] = useState<File | null>(null);
  const [fileHash, setFileHash] = useState<string | null>(null);
  const [duplicateInfo, setDuplicateInfo] = useState<DuplicateInfo | null>(null);
  const [checkingHash, setCheckingHash] = useState(false);
  const [airlineCode, setAirlineCode] = useState("ANG");
  const [scheduleVersion, setScheduleVersion] = useState("");
  const [loading, setLoading] = useState(false);
  const [rawMarkdown, setRawMarkdown] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [header, setHeader] = useState<ScheduleHeader | null>(null);
  const [parsedRows, setParsedRows] = useState<ValidatedFlightPatternRow[]>([]);
  const [cityCodes, setCityCodes] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<ImportProgress | null>(null);
  const [resolvedCount, setResolvedCount] = useState(0);
  const [libraryRefreshKey, setLibraryRefreshKey] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Load city codes on mount for validation
  useEffect(() => {
    const loadCities = async () => {
      const { data, error } = await supabase.from("cities").select("code");
      if (!error && data) {
        setCityCodes(new Set(data.map((c) => c.code.toUpperCase())));
      }
    };
    loadCities();
  }, []);

  const handleFileChange = async (selectedFile: File | null) => {
    setDuplicateInfo(null);
    setFileHash(null);

    if (!selectedFile) {
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setCheckingHash(true);

    try {
      const hash = await generateFileHash(selectedFile);
      setFileHash(hash);

      // Check if hash exists in database
      const { data } = await supabase
        .from("flight_patterns")
        .select("schedule_version, airline_code, imported_at")
        .eq("file_hash", hash)
        .limit(1);

      if (data && data.length > 0) {
        setDuplicateInfo({
          schedule_version: data[0].schedule_version,
          airline_code: data[0].airline_code,
          imported_at: data[0].imported_at || '',
        });
      }
    } catch (err) {
      console.error("Hash generation failed:", err);
    } finally {
      setCheckingHash(false);
    }
  };

  const handleDuplicateCancel = () => {
    setFile(null);
    setFileHash(null);
    setDuplicateInfo(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDuplicateContinue = () => {
    setDuplicateInfo(null);
  };

  const isTimeoutError = (msg: string) =>
    /timed?\s*out|timeout|504|gateway\s*timeout/i.test(msg);

  const handleParse = async () => {
    if (!file) {
      toast({ title: "No file selected", description: "Please select a PDF file.", variant: "destructive" });
      return;
    }
    if (!airlineCode.trim()) {
      toast({ title: "Missing airline code", description: "Please enter an airline code.", variant: "destructive" });
      return;
    }
    if (!scheduleVersion.trim()) {
      toast({ title: "Missing schedule version", description: "Please enter a schedule version.", variant: "destructive" });
      return;
    }

    setLoading(true);
    setRawMarkdown(null);
    setError(null);
    setHeader(null);
    setParsedRows([]);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("airline_code", airlineCode.trim());
      formData.append("schedule_version", scheduleVersion.trim());

      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        setError("You must be logged in as an admin to use this tool.");
        setLoading(false);
        return;
      }

      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/parse-flight-schedule`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      const json = await res.json();

      if (!res.ok) {
        const serverMsg = json.error || `Error ${res.status}`;
        if (res.status === 504 || isTimeoutError(serverMsg)) {
          setError("Parsing timed out. The PDF may be too large or the service is busy. Please try again.");
        } else {
          setError(serverMsg);
        }
      } else {
        const md = typeof json.markdown === "string" ? json.markdown : JSON.stringify(json.markdown, null, 2);
        setRawMarkdown(md);

        const h = extractHeader(md);
        setHeader(h);

        const rows = parseFlightTables(md);
        const validated = validateRows(rows, cityCodes);

        const { rows: resolved, resolvedCount: rc } = await resolveQuestionMarkTimes(validated);
        setResolvedCount(rc);
        setParsedRows([...resolved]);

        toast({ title: "Parse complete", description: `${resolved.length} flight rows extracted${rc > 0 ? `, ${rc} times auto-resolved` : ''}.` });
      }
    } catch (err: unknown) {
      console.error("Parse error:", err);
      const msg = err instanceof Error ? err.message : "Unknown error";
      if (isTimeoutError(msg)) {
        setError("Parsing timed out. The PDF may be too large or the service is busy. Please try again.");
      } else {
        setError(`Parsing failed: ${msg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const isValidTime = (t: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(t);

  const handleTimeChange = (rowIndex: number, field: 'dep_time' | 'arr_time', value: string) => {
    setParsedRows(prev => {
      const updated = [...prev];
      const row = { ...updated[rowIndex] };
      row[field] = value;

      row.warnings = row.warnings.filter(w => !w.includes('dep_time') && !w.includes('arr_time'));

      const depOk = isValidTime(row.dep_time);
      const arrOk = isValidTime(row.arr_time);

      if (!depOk && (row.dep_time === '?' || row.dep_time === '')) {
        row.warnings.push('dep_time unresolved — row excluded from import');
      }
      if (!arrOk && (row.arr_time === '?' || row.arr_time === '')) {
        row.warnings.push('arr_time unresolved — row excluded from import');
      }

      if (depOk && arrOk) {
        row.is_overnight = row.arr_time < row.dep_time;
      }

      row.valid = row.warnings.length === 0;
      updated[rowIndex] = row;
      return updated;
    });
  };

  const needsTimeEdit = (row: ValidatedFlightPatternRow, field: 'dep_time' | 'arr_time') =>
    !row.valid && (!isValidTime(row[field]) || row[field] === '?');

  const validRows = parsedRows.filter((r) => r.valid);
  const validCount = validRows.length;
  const warningCount = parsedRows.filter((r) => !r.valid).length;

  const handleImport = async () => {
    if (!header || validRows.length === 0) return;
    setImporting(true);
    setImportProgress(null);

    const result = await importSchedule(
      validRows,
      header,
      airlineCode.trim(),
      scheduleVersion.trim(),
      (progress) => setImportProgress(progress),
      fileHash || undefined
    );

    setImporting(false);

    if (result.step === 'done') {
      toast({
        title: "Import successful",
        description: result.message,
      });
      setLibraryRefreshKey(k => k + 1);
      setTimeout(() => {
        setFile(null);
        setFileHash(null);
        setDuplicateInfo(null);
        setScheduleVersion("");
        setRawMarkdown(null);
        setError(null);
        setHeader(null);
        setParsedRows([]);
        setImportProgress(null);
        setResolvedCount(0);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }, 2000);
    } else {
      toast({
        title: "Import failed",
        description: result.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="container mx-auto px-4 py-12 max-w-5xl space-y-6">
      {/* Upload Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            NAC Schedule Import Tool
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pdf-file">PDF File</Label>
            <div className="flex items-center gap-2">
              <Input
                id="pdf-file"
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
              />
              {file && !checkingHash && (
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  {file.name}
                </span>
              )}
              {checkingHash && (
                <span className="text-sm text-muted-foreground whitespace-nowrap flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Checking...
                </span>
              )}
            </div>
          </div>

          {/* Duplicate Warning */}
          {duplicateInfo && (
            <Alert className="border-2 border-amber-400 bg-amber-50 dark:bg-amber-950/30">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-semibold text-amber-900 dark:text-amber-200">
                    ⚠️ This PDF has already been imported
                  </p>
                  <p className="text-amber-800 dark:text-amber-300 text-sm">
                    Schedule: <strong>{duplicateInfo.schedule_version}</strong> for{' '}
                    <strong>{duplicateInfo.airline_code}</strong>
                    {duplicateInfo.imported_at && (
                      <> on {new Date(duplicateInfo.imported_at).toLocaleDateString()}</>
                    )}
                  </p>
                  <p className="text-amber-700 dark:text-amber-400 text-sm">
                    Do you want to continue anyway?
                  </p>
                  <div className="flex gap-2 mt-1">
                    <Button variant="outline" size="sm" onClick={handleDuplicateCancel}>
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      onClick={handleDuplicateContinue}
                    >
                      Import Anyway
                    </Button>
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="airline-code">Airline Code</Label>
              <Input
                id="airline-code"
                value={airlineCode}
                onChange={(e) => setAirlineCode(e.target.value)}
                placeholder="e.g. ANG"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="schedule-version">Schedule Version</Label>
              <Input
                id="schedule-version"
                value={scheduleVersion}
                onChange={(e) => setScheduleVersion(e.target.value)}
                placeholder="e.g. SCH-141B"
              />
            </div>
          </div>

          <Button onClick={handleParse} disabled={loading || !!duplicateInfo || !file} className="w-full">
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Parsing PDF...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Parse PDF
              </>
            )}
          </Button>

          {/* Progress indicator while parsing */}
          {loading && (
            <div className="flex items-center gap-3 p-4 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600 dark:text-blue-400 shrink-0" />
              <div>
                <p className="font-medium text-blue-900 dark:text-blue-100">Parsing PDF with AI</p>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  This may take up to 5 minutes for large schedules. Please don't close this page.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="text-destructive font-medium">{error}</p>
                {isTimeoutError(error) && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Tip: If the PDF has many pages, the AI parser may need more time. Try again — it often succeeds on retry.
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Schedule Header */}
      {header && (
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Valid From</Label>
                <p className="font-medium">{header.valid_from || '—'}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Valid To</Label>
                <p className="font-medium">{header.valid_to || '—'}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Schedule Code</Label>
                <p className="font-medium">{header.schedule_code || '—'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary + Preview Table */}
      {parsedRows.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Parsed Flight Patterns</CardTitle>
            <div className="flex items-center gap-4 mt-2">
              <Badge variant="secondary">{parsedRows.length} total rows</Badge>
              <Badge className="bg-green-600 text-white hover:bg-green-700">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                {validCount} valid
              </Badge>
              {resolvedCount > 0 && (
                <Badge variant="outline" className="border-blue-500 text-blue-600 dark:text-blue-400">
                  🔧 {resolvedCount} times auto-resolved
                </Badge>
              )}
              {warningCount > 0 && (
                <Badge variant="destructive">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {warningCount} warnings
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-auto max-h-[600px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Flight</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>Dep</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>Arr</TableHead>
                    <TableHead>Days</TableHead>
                    <TableHead>Aircraft</TableHead>
                    <TableHead>Overnight</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedRows.map((row, idx) => (
                    <TableRow
                      key={idx}
                      className={row.valid ? "bg-green-50 dark:bg-green-950/20" : "bg-red-50 dark:bg-red-950/20"}
                    >
                      <TableCell className="font-mono font-medium">{row.flight_number}</TableCell>
                      <TableCell className="font-mono">{row.dep_airport}</TableCell>
                      <TableCell>
                        {needsTimeEdit(row, 'dep_time') ? (
                          <input
                            type="time"
                            value={row.dep_time === '?' ? '' : row.dep_time}
                            onChange={(e) => handleTimeChange(idx, 'dep_time', e.target.value)}
                            className="bg-amber-100 dark:bg-amber-900/40 border-2 border-amber-400 dark:border-amber-600 rounded px-2 py-1 text-sm w-24"
                          />
                        ) : (
                          row.dep_time
                        )}
                      </TableCell>
                      <TableCell className="font-mono">{row.arr_airport}</TableCell>
                      <TableCell>
                        {needsTimeEdit(row, 'arr_time') ? (
                          <input
                            type="time"
                            value={row.arr_time === '?' ? '' : row.arr_time}
                            onChange={(e) => handleTimeChange(idx, 'arr_time', e.target.value)}
                            className="bg-amber-100 dark:bg-amber-900/40 border-2 border-amber-400 dark:border-amber-600 rounded px-2 py-1 text-sm w-24"
                          />
                        ) : (
                          row.arr_time
                        )}
                      </TableCell>
                      <TableCell className="text-xs">{row.days_display}</TableCell>
                      <TableCell className="text-xs">{row.aircraft_type}</TableCell>
                      <TableCell>{row.is_overnight ? "🌙" : "—"}</TableCell>
                      <TableCell>
                        {row.valid ? (
                          <span className="flex items-center gap-1 text-green-700 dark:text-green-400 text-xs">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Valid
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-destructive text-xs" title={row.warnings.join('; ')}>
                            <AlertTriangle className="h-3.5 w-3.5" /> Check
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {importProgress && (
              <div className={`mt-4 p-3 rounded-md text-sm font-medium ${
                importProgress.step === 'error' ? 'bg-destructive/10 text-destructive' :
                importProgress.step === 'done' ? 'bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-400' :
                'bg-muted text-muted-foreground'
              }`}>
                {importProgress.message}
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <Button
                onClick={handleImport}
                disabled={validCount === 0 || importing}
                className="gap-2"
              >
                {importing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Import className="h-4 w-4" />
                    Confirm &amp; Import
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Raw Markdown (collapsible) */}
      {rawMarkdown && (
        <details className="group">
          <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground transition-colors">
            Show raw markdown output
          </summary>
          <Card className="mt-2">
            <CardContent className="pt-4">
              <pre className="bg-muted p-4 rounded-md text-sm overflow-auto max-h-[400px] whitespace-pre-wrap">
                {rawMarkdown}
              </pre>
            </CardContent>
          </Card>
        </details>
      )}

      {/* Schedule Library */}
      <ScheduleLibrary refreshKey={libraryRefreshKey} />

      {/* Cache Status */}
      <CacheStatus refreshKey={libraryRefreshKey} />
    </div>
  );
}
